/**
 * TaskSettingSwitchGuard — Deep-Link-Schutz für Aufgaben-Flächen, die die Firma in den
 * Aufgaben-Einstellungen abschalten kann (`/tasks/routines`, `/tasks/lage/nachweise`,
 * `/tasks/asset`).
 *
 * Liest `route.data.settingSwitch` (ein boolescher Schlüssel aus {@link TaskSettings}, z. B.
 * `routinesEnabled`). Ist der Schalter aus, geht es zurück auf `/tasks` — dort entscheidet
 * das Rollen-Landing (Manager → Übersicht, Mitarbeiter → „Meine Aufgaben"). Dazu ein
 * Hinweis-Toast (`TASK_SETTING_DISABLED`), damit der Rücksprung nicht wie ein Fehler wirkt:
 * Wer einen Objekt-Aufkleber scannt, soll erfahren, warum er nicht beim Objekt landet.
 *
 * **Abgrenzung:** Der {@link TasksTierGuard} fragt den *Tarif* (Obergrenze, → Abo-Seite),
 * dieser Guard die *Firmen-Einstellung* (zusätzliche Einschränkung, → `/tasks`). Beide hängen
 * unabhängig an denselben Routen; die Einstellung steht zuletzt, damit ein nicht gebuchter
 * Tarif weiterhin auf die Abo-Seite führt statt auf einen Hinweis zu einem Schalter, den
 * niemand einschalten kann.
 *
 * **Fail-open:** Kommen die Einstellungen nicht, liefert der Dienst die Defaults (= an). Die
 * eigentliche Sperre sitzt auf dem Server (403 `TASK_SETTING_DISABLED`).
 */
import { Injectable, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { TaskSettingsService } from '../settings/task-settings.service';
import type { TaskSettings } from '../settings/task-settings.model';

/** Die booleschen Schalter der Aufgaben-Einstellungen. */
export type TaskSettingSwitchKey = {
  [K in keyof TaskSettings]: TaskSettings[K] extends boolean ? K : never;
}[keyof TaskSettings];

/**
 * Reine Entscheidung: Ist der Schalter an? Fehlende Einstellungen, ein fehlender Schlüssel
 * oder ein nicht-boolescher Wert gelten als „an" (fail-open — dieselbe Regel wie beim Tarif).
 */
export function isTaskSettingSwitchOn(
  settings: Partial<TaskSettings> | null | undefined,
  key: TaskSettingSwitchKey | string | null | undefined,
): boolean {
  if (!settings || !key) return true;
  const value = (settings as Record<string, unknown>)[key];
  return value !== false;
}

@Injectable({ providedIn: 'root' })
export class TaskSettingSwitchGuard implements CanActivate {
  /**
   * `toastr`/`translate` sind optional und stehen zuletzt: die Specs bauen den Guard
   * positionell per `new` — ohne sie fällt nur der Hinweis weg, nicht die Weiterleitung.
   */
  constructor(
    private readonly taskSettings: TaskSettingsService,
    private readonly router: Router,
    @Optional() private readonly toastr?: ToastrService,
    @Optional() private readonly translate?: TranslateService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const key = route.data?.['settingSwitch'] as TaskSettingSwitchKey | undefined;
    // Ohne konfigurierten Schalter nichts zu prüfen — eine vergessene `data`-Angabe darf
    // keine Route unerreichbar machen.
    if (!key) return of(true);

    return this.taskSettings.ensureLoaded().pipe(
      map((settings) => {
        if (isTaskSettingSwitchOn(settings, key)) return true;
        const text = this.translate?.instant('TASK_SETTING_DISABLED');
        if (text) this.toastr?.info(text);
        return this.router.createUrlTree(['/tasks']);
      }),
    );
  }
}
